import React, { useState } from "react";
import { Modal, Button, Form, Tabs, Alert, Tab } from "react-bootstrap";
import { useToast } from "../contexts/ToastContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTable, faDownload, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import "./styles/MaxDiffConfigComponent.css";

const MaxDiffConfigComponent = ({ 
  oeParameters, 
  setOeParameters, 
  items,
  onConfigUpdate,handleChange,
  showMaxDiffModal,
  setShowMaxDiffModal,maxDiffDesign,
  setMaxDiffDesign 
}) => {
  const { toast } = useToast();

  // Generate balanced MaxDiff design
  const generateBalancedDesign = () => {
    const { itemsPerSet, tasksPerRespondent, designVersions } = maxDiffDesign;
    const totalItems = items?.length || 0;
    
    if (totalItems < itemsPerSet) {
      toast.warning(`Need at least ${itemsPerSet} items for MaxDiff design.`);
      return null;
    }

    // Create balanced design
    const design = [];
    const tasksPerVersion = Math.ceil(tasksPerRespondent / designVersions);
    
    for (let version = 0; version < designVersions; version++) {
      const versionDesign = [];
      
      for (let task = 0; task < tasksPerVersion; task++) {
        const set = [];
        const usedIndices = new Set();
        
        // Create a balanced set
        while (set.length < itemsPerSet) {
          const randomIndex = Math.floor(Math.random() * totalItems);
          if (!usedIndices.has(randomIndex)) {
            const item = items[randomIndex];
            set.push({
              code: item.code || `ITEM_${randomIndex + 1}`,
              text: item.text || item.label || `Item ${randomIndex + 1}`
            });
            usedIndices.add(randomIndex);
          }
        }
        
        versionDesign.push({
          taskNumber: task + 1,
          items: set
        });
      }
      design.push({
        version: version + 1,
        tasks: versionDesign
      });
    }
    
    return design;
  };

  // Handle design creation
  const handleCreateDesign = () => {
    const design = generateBalancedDesign();
    if (!design) return;

    setOeParameters({
      ...oeParameters,
      maxDiffDesign: {
        ...maxDiffDesign,
        generatedDesign: design,
        designMethod: 'created',
        totalItems: items.length,
        designSummary: `Balanced design with ${maxDiffDesign.itemsPerSet} items per set, ${maxDiffDesign.tasksPerRespondent} tasks, ${maxDiffDesign.designVersions} versions`
      }
    });
    
    setShowMaxDiffModal(false);
    toast.success("MaxDiff design configured successfully!");
  };

  // Handle design paste
  // Handle design paste with auto-detection
const handlePasteDesign = () => {
  if (!maxDiffDesign.designData.trim()) {
    toast.warning("Please paste design data.");
    return;
  }

  try {
    const design = parseDesignData(maxDiffDesign.designData);
    
    if (design.length === 0) {
      toast.warning("No valid design data found. Please check the format.");
      return;
    }
    
    // Auto-detect all parameters from parsed design
    const versions = design.length;
    const tasksPerVersion = Math.max(...design.map(version => version.tasks.length));
    const itemsPerSet = design[0]?.tasks[0]?.items.length || 0;
    const totalTasks = design.reduce((sum, version) => sum + version.tasks.length, 0);
    
    // Find all unique items used in the design
    const allItems = new Set();
    design.forEach(version => {
      version.tasks.forEach(task => {
        task.items.forEach(item => {
          allItems.add(item.originalIndex || item.code);
        });
      });
    });
    
    handleChange('maxDiffDesign', {
        ...maxDiffDesign,
        generatedDesign: design,
        designMethod: 'pasted',
        designSummary: `Custom design detected: ${versions} versions, ${tasksPerVersion} tasks/version, ${itemsPerSet} items/set, ${totalTasks} total tasks`,
        actualVersions: versions,
        actualTasksPerVersion: tasksPerVersion,
        actualItemsPerSet: itemsPerSet,
        totalTasks: totalTasks,
        uniqueItemsUsed: allItems.size
      });
    setShowMaxDiffModal(false);
    toast.success("MaxDiff design configured successfully!");
  } catch (error) {
    console.error('Parse error:', error);
    toast.error("Error parsing design data. Please check the format.");
  }
};

  // Parse pasted design data
// Parse pasted design data dynamically
const parseDesignData = (data) => {
  const lines = data.trim().split('\n').filter(line => line.trim());
  const design = [];
  
  // Detect separator and structure
  const firstLine = lines[0];
  const hasHeader = firstLine.toLowerCase().includes('version') || firstLine.toLowerCase().includes('set');
  const separator = firstLine.includes(',') ? ',' : /\s+/; // Detect comma or whitespace
  
  let versionCol = 0, setCol = 1, itemsStartCol = 2;
  
  // Auto-detect column positions from header if available
  if (hasHeader) {
    const headers = firstLine.split(separator).map(h => h.toLowerCase().trim());
    versionCol = headers.findIndex(h => h.includes('version'));
    setCol = headers.findIndex(h => h.includes('set') || h.includes('task'));
    itemsStartCol = Math.max(versionCol, setCol) + 1;
  }
  
  lines.forEach((line, index) => {
    if (index === 0 && hasHeader) return; // Skip header
    
    const columns = line.split(separator).map(col => col.trim()).filter(col => col);
    
    if (columns.length >= itemsStartCol + 1) { // At least version + set + 1 item
      const version = parseInt(columns[versionCol]) || 1;
      const set = parseInt(columns[setCol]) || 1;
      
      // Dynamically find all item columns (numeric values after set column)
      const itemCodes = [];
      for (let i = itemsStartCol; i < columns.length; i++) {
        const potentialItem = columns[i];
        if (potentialItem && !isNaN(parseInt(potentialItem))) {
          itemCodes.push(potentialItem);
        }
      }
      
      if (itemCodes.length > 0) {
        if (!design[version - 1]) {
          design[version - 1] = {
            version: version,
            tasks: []
          };
        }
        
        const taskItems = itemCodes.map(itemCode => {
          const itemIndex = parseInt(itemCode) - 1; // Convert to 0-based index
          const foundItem = items?.[itemIndex]; // Get item by index
          
          return foundItem || {
            code: `ITEM_${itemCode}`,
            text: `Item ${itemCode}`,
            originalIndex: itemCode
          };
        }).filter(Boolean);
        
        design[version - 1].tasks.push({
          taskNumber: set,
          items: taskItems
        });
      }
    }
  });
  
  return design.filter(version => version && version.tasks.length > 0);
};

  // Download design template
  const downloadDesignTemplate = () => {
    const header = "Version,Task,Item1,Item2,Item3,Item4,Item5";
    const exampleRows = [
      "1,1,ITEM_1,ITEM_2,ITEM_3,ITEM_4,ITEM_5",
      "1,2,ITEM_6,ITEM_7,ITEM_8,ITEM_9,ITEM_10",
      "2,1,ITEM_1,ITEM_3,ITEM_5,ITEM_7,ITEM_9",
      "2,2,ITEM_2,ITEM_4,ITEM_6,ITEM_8,ITEM_10"
    ];
    
    const template = [header, ...exampleRows].join('\n');
    
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'maxdiff_design_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Calculate design statistics
  // Calculate design statistics with auto-detection
const getDesignStats = () => {
  const totalItems = items?.length || 0;
  
  // Use auto-detected stats from parsed design if available
  if (oeParameters?.maxDiffDesign?.generatedDesign) {
    const design = oeParameters.maxDiffDesign.generatedDesign;
    const versions = design.length;
    const totalTasks = design.reduce((sum, version) => sum + version.tasks.length, 0);
    const itemsPerSet = design[0]?.tasks[0]?.items.length || 0;
    const tasksPerVersion = Math.max(...design.map(version => version.tasks.length));
    
    return {
      totalItems,
      totalTasks,
      totalVersions: versions,
      itemsPerSet,
      tasksPerVersion,
      designMethod: 'pasted',
      isAutoDetected: true
    };
  }
  
  // Fallback to form values for created designs
  const { itemsPerSet, tasksPerRespondent, designVersions } = maxDiffDesign;
  const totalTasks = tasksPerRespondent * designVersions;
  
  return {
    totalItems,
    totalTasks,
    totalVersions: designVersions,
    itemsPerSet,
    tasksPerVersion: tasksPerRespondent,
    designMethod: 'created',
    isAutoDetected: false
  };
};

  const stats = getDesignStats();

  return (
    <>
      {/* MaxDiff Design Modal */}
      <Modal 
        show={showMaxDiffModal} 
        onHide={() => setShowMaxDiffModal(false)} 
        size="lg"
        className="maxdiff-config-modal"
      >
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>
            <FontAwesomeIcon icon={faTable} className="me-2" />
            MaxDiff Design Configuration
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body>
          {/* Design Statistics */}

<Alert variant="info" className="mb-4">
  <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
  <strong>Design Overview:</strong> {stats.totalItems} total items, {stats.totalTasks} total tasks, 
  {stats.itemsPerSet} items per set{stats.isAutoDetected && <span className="text-success"> (auto-detected)</span>}
</Alert>

          <Tabs 
            defaultActiveKey="create" 
            className="mb-3"
            onSelect={(tab) => setMaxDiffDesign({...maxDiffDesign, method: tab})}
          >
            {/* Create Design Tab */}
            <Tab eventKey="create" title="Create Design">
              <div className="create-design-form">
                <Form.Group className="mb-3">
                  <Form.Label>
                    <strong>Design Type</strong>
                  </Form.Label>
                  <Form.Select
                    value={maxDiffDesign.designType}
                    onChange={(e) => setMaxDiffDesign({
                      ...maxDiffDesign,
                      designType: e.target.value
                    })}
                  >
                    <option value="balanced">Balanced Incomplete Block Design (Recommended)</option>
                    <option value="random">Random Sampling</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Balanced design ensures each item appears approximately equally often
                  </Form.Text>
                </Form.Group>

                <div className="design-parameters-grid">
                  <Form.Group className="mb-3">
                    <Form.Label>Items per Set</Form.Label>
                    <Form.Control
                      type="number"
                      value={maxDiffDesign.itemsPerSet}
                      onChange={(e) => setMaxDiffDesign({
                        ...maxDiffDesign,
                        itemsPerSet: parseInt(e.target.value) || 5
                      })}
                      min="3"
                      max="8"
                    />
                    <Form.Text className="text-muted">
                      Typically 4-6 items per set. Affects cognitive load.
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Tasks per Respondent</Form.Label>
                    <Form.Control
                      type="number"
                      value={maxDiffDesign.tasksPerRespondent}
                      onChange={(e) => setMaxDiffDesign({
                        ...maxDiffDesign,
                        tasksPerRespondent: parseInt(e.target.value) || 10
                      })}
                      min="5"
                      max="20"
                    />
                    <Form.Text className="text-muted">
                      More tasks = better data, but may cause fatigue
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Design Versions</Form.Label>
                    <Form.Control
                      type="number"
                      value={maxDiffDesign.designVersions}
                      onChange={(e) => setMaxDiffDesign({
                        ...maxDiffDesign,
                        designVersions: parseInt(e.target.value) || 3
                      })}
                      min="1"
                      max="10"
                    />
                    <Form.Text className="text-muted">
                      Different versions to rotate across respondents
                    </Form.Text>
                  </Form.Group>
                </div>

                {/* Design Preview */}
                <div className="design-preview-card p-3 bg-light rounded">
                  <h6 className="mb-3">Design Preview</h6>
                  <div className="preview-stats">
                    <div className="stat-item">
                      <strong>Total Items:</strong> {stats.totalItems}
                    </div>
                    <div className="stat-item">
                      <strong>Items per Set:</strong> {maxDiffDesign.itemsPerSet}
                    </div>
                    <div className="stat-item">
                      <strong>Tasks per Version:</strong> {maxDiffDesign.tasksPerRespondent}
                    </div>
                    <div className="stat-item">
                      <strong>Total Versions:</strong> {maxDiffDesign.designVersions}
                    </div>
                    <div className="stat-item">
                      <strong>Total Tasks:</strong> {stats.totalTasks}
                    </div>
                  </div>
                </div>
              </div>
            </Tab>

            {/* Paste Design Tab */}
            <Tab eventKey="paste" title="Paste Design">
              <div className="paste-design-form">
                <Alert variant="warning" className="mb-3">
                  <small>
                    <strong>Format Requirements:</strong> CSV with columns: Version, Task, Item1, Item2, Item3, Item4, Item5
                  </small>
                </Alert>

                <Form.Group className="mb-3">
                  <Form.Label>
                    <strong>Design Data (CSV Format)</strong>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={12}
                    value={maxDiffDesign.designData}
                    onChange={(e) => setMaxDiffDesign({
                      ...maxDiffDesign,
                      designData: e.target.value
                    })}
                    placeholder={`Version,Task,Item1,Item2,Item3,Item4,Item5\n1,1,ITEM_1,ITEM_2,ITEM_3,ITEM_4,ITEM_5\n1,2,ITEM_6,ITEM_7,ITEM_8,ITEM_9,ITEM_10`}
                    className="font-monospace"
                  />
                  <Form.Text className="text-muted">
                    Use item codes or exact text from your item list
                  </Form.Text>
                </Form.Group>

                <div className="d-flex justify-content-between align-items-center">
                  <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    onClick={downloadDesignTemplate}
                  >
                    <FontAwesomeIcon icon={faDownload} className="me-1" />
                    Download Template
                  </Button>
                  
                  <div className="text-muted">
                    <small>Items found in paste: {maxDiffDesign.designData ? maxDiffDesign.designData.split('\n').length - 1 : 0} sets</small>
                  </div>
                </div>
              </div>
            </Tab>
          </Tabs>
        </Modal.Body>
        
        <Modal.Footer>
          <Button 
            variant="outline-secondary" 
            onClick={() => setShowMaxDiffModal(false)}
          >
            Cancel
          </Button>
          
          {maxDiffDesign.method === "create" ? (
            <Button 
              variant="primary" 
              onClick={handleCreateDesign}
              disabled={!items || items.length < maxDiffDesign.itemsPerSet}
            >
              Generate Balanced Design
            </Button>
          ) : (
            <Button 
              variant="primary" 
              onClick={handlePasteDesign}
              disabled={!maxDiffDesign.designData.trim()}
            >
              Use Pasted Design
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MaxDiffConfigComponent;